import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import {
  FiSearch, FiTrash2, FiAlertTriangle, FiChevronLeft, FiChevronRight,
  FiChevronDown, FiFileText, FiShoppingBag,
} from 'react-icons/fi'
import './Commande.css'

const ITEMS_PER_PAGE = 10
const API = process.env.REACT_APP_API_URL

const STATUTS = [
  { value: 'En attente', label: 'En attente', color: 'orange' },
  { value: 'Traitée', label: 'Traitée', color: 'green' },
  { value: 'Annulée', label: 'Annulée', color: 'red' },
]

const getStatut = (val) => STATUTS.find((s) => s.value === val) || STATUTS[0]

const TableCommande = () => {
  const [commandes, setCommandes] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatut, setFilterStatut] = useState('all')
  const [sortBy, setSortBy] = useState('recent')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [statutLoading, setStatutLoading] = useState(null)
  const [expanded, setExpanded] = useState(null)

  const authorization = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${authorization}` }
  const navigate = useNavigate()

  const handleAuthError = (err) => {
    if ([401, 403, 404].includes(err.response?.status)) {
      localStorage.clear()
      navigate('/login')
    }
  }

  const fetchData = () => {
    setLoading(true)
    axios.get(`${API}/commande/lister`, { headers })
      .then((res) => setCommandes(Array.isArray(res.data) ? res.data : []))
      .catch((err) => handleAuthError(err))
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [])

  const filtered = useMemo(() => {
    let arr = [...commandes]
    if (search.trim()) {
      const s = search.toLowerCase()
      arr = arr.filter((c) =>
        c.nom?.toLowerCase().includes(s) ||
        c.prenom?.toLowerCase().includes(s) ||
        c.adresse?.toLowerCase().includes(s) ||
        String(c.numero || '').includes(s)
      )
    }
    if (filterStatut !== 'all') arr = arr.filter((c) => (c.statut || 'En attente') === filterStatut)
    if (sortBy === 'ancien') arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    else arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    return arr
  }, [commandes, search, filterStatut, sortBy])

  const nbPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE))
  const pageData = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  useEffect(() => { setPage(1) }, [search, filterStatut, sortBy])

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const toggleSelectAll = () => setSelected(selected.length === pageData.length ? [] : pageData.map((c) => c._id))

  const handleStatut = async (id, statut) => {
    setStatutLoading(id)
    try {
      await axios.post(`${API}/commande/${id}/statut`, { statut }, { headers })
      setCommandes((prev) => prev.map((c) => (c._id === id ? { ...c, statut } : c)))
      toast.success('Statut mis à jour')
    } catch (err) {
      toast.error('Échec de la mise à jour du statut')
      handleAuthError(err)
    } finally {
      setStatutLoading(null)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await axios.get(`${API}/commande/${deleteTarget._id}/delete`, { headers })
      setCommandes((c) => c.filter((x) => x._id !== deleteTarget._id))
      toast.success('Commande supprimée')
      setDeleteTarget(null)
    } catch (err) {
      toast.error('Échec de la suppression')
      handleAuthError(err)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!selected.length) return
    if (!window.confirm(`Supprimer ${selected.length} commande(s) ?`)) return
    try {
      await Promise.all(selected.map((id) => axios.get(`${API}/commande/${id}/delete`, { headers })))
      setCommandes((c) => c.filter((x) => !selected.includes(x._id)))
      setSelected([])
      toast.success('Commandes supprimées')
    } catch (err) {
      toast.error('Échec de la suppression groupée')
      handleAuthError(err)
    }
  }

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
  const totalArticles = (articles) => articles?.reduce((t, a) => t + (a.quantity || 1), 0) || 0
  const totalPrix = (articles) => articles?.reduce((t, a) => t + (a.price || 0) * (a.quantity || 1), 0) || 0

  const stats = STATUTS.map((s) => ({ ...s, count: commandes.filter((c) => (c.statut || 'En attente') === s.value).length }))

  return (
    <div className="acmd-page">
      <Toaster />

      <div className="acmd-header">
        <div>
          <h1 className="acmd-title">Commandes</h1>
          <p className="acmd-subtitle">{loading ? 'Chargement…' : `${filtered.length} commande${filtered.length > 1 ? 's' : ''}`}</p>
        </div>
      </div>

      {!loading && (
        <div className="acmd-stats">
          {stats.map((s) => (
            <button
              key={s.value}
              className={`acmd-stat ${filterStatut === s.value ? 'acmd-stat--active' : ''}`}
              onClick={() => setFilterStatut((prev) => (prev === s.value ? 'all' : s.value))}
            >
              <span className={`acmd-stat__dot acmd-dot--${s.color}`} />
              <span className="acmd-stat__num">{s.count}</span>
              <span className="acmd-stat__label">{s.label}</span>
            </button>
          ))}
        </div>
      )}

      <div className="acmd-toolbar">
        <div className="acmd-search">
          <FiSearch size={15} />
          <input
            type="text"
            placeholder="Rechercher par nom, adresse, téléphone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="acmd-search__clear" onClick={() => setSearch('')}>✕</button>}
        </div>

        <select className="acmd-select" value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}>
          <option value="all">Tous les statuts</option>
          {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <select className="acmd-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="recent">Plus récentes</option>
          <option value="ancien">Plus anciennes</option>
        </select>

        {selected.length > 0 && (
          <button className="acmd-bulk-delete" onClick={handleBulkDelete}>
            <FiTrash2 size={14} />
            Supprimer ({selected.length})
          </button>
        )}
      </div>

      <div className="acmd-table-wrap">
        <table className="acmd-table">
          <thead>
            <tr>
              <th className="acmd-th-check">
                <input type="checkbox" checked={pageData.length > 0 && selected.length === pageData.length} onChange={toggleSelectAll} />
              </th>
              <th>Client</th>
              <th>Téléphone</th>
              <th>Adresse</th>
              <th>Articles</th>
              <th>Total</th>
              <th>Statut</th>
              <th>Date</th>
              <th className="acmd-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="acmd-skeleton-row"><td colSpan={9}><div className="acmd-skeleton" /></td></tr>
              ))
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={9} className="acmd-empty">
                  <FiShoppingBag size={36} />
                  <p>Aucune commande trouvée</p>
                </td>
              </tr>
            ) : (
              pageData.map((c) => {
                const statutInfo = getStatut(c.statut)
                return (
                  <React.Fragment key={c._id}>
                    <tr className={selected.includes(c._id) ? 'acmd-row--selected' : ''}>
                      <td className="acmd-th-check">
                        <input type="checkbox" checked={selected.includes(c._id)} onChange={() => toggleSelect(c._id)} />
                      </td>
                      <td>
                        <div className="acmd-client">
                          <div className="acmd-client__avatar">{c.prenom?.[0]?.toUpperCase()}</div>
                          <span className="acmd-client__name">{c.prenom} {c.nom}</span>
                        </div>
                      </td>
                      <td><span className="acmd-tel">{c.numero}</span></td>
                      <td><span className="acmd-adresse">{c.adresse}</span></td>
                      <td>
                        <button className="acmd-articles-btn" onClick={() => setExpanded(expanded === c._id ? null : c._id)}>
                          {totalArticles(c.article)} article{totalArticles(c.article) > 1 ? 's' : ''}
                          <FiChevronDown size={11} style={{ transform: expanded === c._id ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                        </button>
                      </td>
                      <td><span className="acmd-prix">{totalPrix(c.article)} DT</span></td>
                      <td>
                        <div className="acmd-etat-wrap">
                          {statutLoading === c._id ? (
                            <span className="acmd-spin" />
                          ) : (
                            <select
                              className={`acmd-etat-select acmd-etat--${statutInfo.color}`}
                              value={c.statut || 'En attente'}
                              onChange={(e) => handleStatut(c._id, e.target.value)}
                            >
                              {STATUTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          )}
                        </div>
                      </td>
                      <td><span className="acmd-date">{formatDate(c.createdAt)}</span></td>
                      <td className="acmd-th-actions">
                        <button className="acmd-action-btn acmd-action-btn--delete" onClick={() => setDeleteTarget(c)} title="Supprimer">
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>

                    {expanded === c._id && (
                      <tr className="acmd-articles-row">
                        <td colSpan={9}>
                          <div className="acmd-articles">
                            <div className="acmd-articles__header">Détail des articles</div>
                            {c.article?.map((art, i) => (
                              <div key={i} className="acmd-article-item">
                                {art.image && <img src={art.image} alt={art.titre} className="acmd-article-item__img" />}
                                <div className="acmd-article-item__info">
                                  <p className="acmd-article-item__title">{art.titre}</p>
                                  {art.dimension2 && <p className="acmd-article-item__dim">{art.dimension2}</p>}
                                </div>
                                <div className="acmd-article-item__right">
                                  <span className="acmd-article-item__qty">×{art.quantity || 1}</span>
                                  <span className="acmd-article-item__price">{(art.price || 0) * (art.quantity || 1)} DT</span>
                                </div>
                              </div>
                            ))}
                            {c.remarque && (
                              <div className="acmd-remarque">
                                <FiFileText size={13} />
                                <span>{c.remarque}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && nbPages > 1 && (
        <div className="acmd-pagination">
          <button className="acmd-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}><FiChevronLeft /></button>
          {Array.from({ length: nbPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`acmd-page-btn ${p === page ? 'acmd-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="acmd-page-btn" disabled={page === nbPages} onClick={() => setPage((p) => p + 1)}><FiChevronRight /></button>
        </div>
      )}

      {deleteTarget && (
        <div className="acmd-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="acmd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="acmd-modal__icon"><FiAlertTriangle size={22} /></div>
            <h3 className="acmd-modal__title">Supprimer cette commande ?</h3>
            <p className="acmd-modal__text">La commande de <strong>{deleteTarget.prenom} {deleteTarget.nom}</strong> sera supprimée définitivement.</p>
            <div className="acmd-modal__actions">
              <button className="acmd-modal__btn acmd-modal__btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</button>
              <button className="acmd-modal__btn acmd-modal__btn--confirm" onClick={handleDelete} disabled={deleting}>{deleting ? 'Suppression…' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableCommande
